import { queryClient } from '@/query-client';
import { useQuery } from '@tanstack/react-query';

import {
  type CreateProjectRequest,
  type DeleteProjectsQuery,
  type Project,
  ProjectSchema,
  type Projects,
  type ProjectsQuery,
  ProjectsSchema,
  type UpdateProjectRequest,
} from '@mai/types';

import { apiClient } from './axios';

import { logger } from '@utils/logger';

const PROJECTS_QUERY_KEY = 'projects';

export const useProjectsQuery = (params: ProjectsQuery, enabled: boolean) => {
  return useQuery({
    queryKey: [PROJECTS_QUERY_KEY, params],
    queryFn: async () => {
      const response = await apiClient.get<Projects>('/projects', { params });
      const validatedData = ProjectsSchema.safeParse(response.data);
      if (!validatedData.success) {
        logger.warn('failed to validate projects data', {
          error: validatedData.error,
        });
      }
      return validatedData.data;
    },
    enabled,
  });
};

export const useProjectQuery = (projectId: string | undefined | null) => {
  return useQuery({
    queryKey: [PROJECTS_QUERY_KEY, projectId],
    queryFn: async () => {
      const response = await apiClient.get<Project>(`/projects/${projectId}`);
      const validatedData = ProjectSchema.safeParse(response.data);
      if (!validatedData.success) {
        logger.warn('failed to validate project data', {
          error: validatedData.error,
        });
        return response.data;
      }
      return validatedData.data;
    },
    enabled: !!projectId,
  });
};

export const createProject = async (project: CreateProjectRequest) => {
  const response = await apiClient.post<Project>('/projects', project);
  const validatedData = ProjectSchema.safeParse(response.data);
  if (!validatedData.success) {
    logger.warn('failed to validate project data', {
      error: validatedData.error,
    });
    return response.data;
  }
  void queryClient.invalidateQueries({ queryKey: [PROJECTS_QUERY_KEY] });
  return validatedData.data;
};

export const deleteProject = async (projectId: string) => {
  await apiClient.delete(`/projects/${projectId}`);
  void queryClient.invalidateQueries({ queryKey: [PROJECTS_QUERY_KEY] });
};

export const deleteProjects = async (params: DeleteProjectsQuery) => {
  await apiClient.delete('/projects', { params });
  void queryClient.invalidateQueries({ queryKey: [PROJECTS_QUERY_KEY] });
};

export const updateProject = async (
  projectId: string,
  params: UpdateProjectRequest,
) => {
  const response = await apiClient.put<Project>(
    `/projects/${projectId}`,
    params,
  );
  const validatedData = ProjectSchema.safeParse(response.data);
  if (!validatedData.success) {
    logger.warn('failed to validate project data', {
      error: validatedData.error,
    });
    return response.data;
  }
  void queryClient.invalidateQueries({ queryKey: [PROJECTS_QUERY_KEY] });
  return validatedData.data;
};
